/**
 * Scoring methodology (labels, measures and weights come from the genre config).
 *
 * The model scores six dimensions with a one-line reason each; the overall
 * rating is computed here, never chosen by the model, so every published
 * number comes from the same formula. Cultural impact is deliberately not a
 * dimension: it rewards popularity and cannot be judged for new releases. It
 * belongs in the text, not in the score.
 */

import { SITE } from '@/site';

export type ScoreDimensionKey =
  | 'writing'
  | 'production'
  | 'delivery'
  | 'cohesion'
  | 'originality'
  | 'replay';

export interface ScoreDimension {
  key: ScoreDimensionKey;
  label: string;
  weight: number;
  /** What the dimension measures, shown on the methodology page and in the prompt. */
  measures: string;
}

export interface DimensionScore {
  score: number;
  note: string;
}

export type Scorecard = Record<ScoreDimensionKey, DimensionScore>;

export const SCORECARD_VERSION = 1;

export const SCORE_DIMENSIONS: readonly ScoreDimension[] = SITE.scorecard.dimensions;

/** Bands used for every dimension and for the overall rating. */
export const SCORE_BANDS: readonly { min: number; label: string; meaning: string }[] = [
  { min: 9.5, label: 'All-time', meaning: SITE.scorecard.topBandMeaning },
  { min: 8.5, label: 'Essential', meaning: 'Required listening; flaws are minor and few.' },
  { min: 7.5, label: 'Great', meaning: 'Clearly above the genre\'s good records; worth owning.' },
  { min: 6.5, label: 'Good', meaning: 'Solid, with real strengths and noticeable weaknesses.' },
  { min: 5.5, label: 'Mixed', meaning: 'Strengths and weaknesses roughly cancel out.' },
  { min: 4.0, label: 'Weak', meaning: 'More misses than hits.' },
  { min: 0, label: 'Poor', meaning: 'Fails at what it attempts.' },
] as const;

export function scoreBand(score: number): (typeof SCORE_BANDS)[number] {
  return SCORE_BANDS.find((b) => score >= b.min) ?? SCORE_BANDS[SCORE_BANDS.length - 1];
}

function clampScore(value: number): number {
  if (!Number.isFinite(value)) return 0;
  return Math.min(10, Math.max(0, Math.round(value * 2) / 2));
}

/** Normalizes model output: scores to 0-10 in half-point steps. */
export function normalizeScorecard(raw: Scorecard | null | undefined): Scorecard {
  return Object.fromEntries(
    SCORE_DIMENSIONS.map(({ key }) => [
      key,
      { score: clampScore(raw?.[key]?.score ?? 0), note: (raw?.[key]?.note ?? '').trim() },
    ]),
  ) as Scorecard;
}

/** Weighted overall rating, one decimal. */
export function computeOverallRating(card: Scorecard): number {
  const total = SCORE_DIMENSIONS.reduce((sum, d) => sum + clampScore(card[d.key].score) * d.weight, 0);
  return Math.round(total * 10) / 10;
}

export function isScorecard(value: unknown): value is Scorecard {
  if (!value || typeof value !== 'object') return false;
  return SCORE_DIMENSIONS.every((d) => {
    const entry = (value as Record<string, unknown>)[d.key] as DimensionScore | undefined;
    return entry != null && typeof entry.score === 'number' && typeof entry.note === 'string';
  });
}

/** Rubric text for the review prompt; generated so prompt and page never drift apart. */
export function scorecardPromptRubric(): string {
  const dims = SCORE_DIMENSIONS.map(
    (d) => `• ${d.key} (${d.label}, ${Math.round(d.weight * 100)}%): ${d.measures}`,
  ).join('\n');
  const bands = SCORE_BANDS.map((b) => `• ${b.min.toFixed(1)}+ ${b.label}: ${b.meaning}`).join('\n');
  return `${dims}\n\nBands (same for every dimension)\n${bands}`;
}

/**
 * Median of each dimension across independent scoring runs, with the notes of
 * the published card. One run that lands high or low no longer decides the
 * rating (measured 2026-10-11: a third of albums moved 0.3 or more between
 * runs). With no runs the published scores stand.
 */
export function medianScorecard(published: Scorecard, runs: readonly Record<ScoreDimensionKey, number>[]): Scorecard {
  const median = (values: number[]) => {
    const sorted = [...values].sort((a, b) => a - b);
    const mid = Math.floor(sorted.length / 2);
    return sorted.length % 2 ? sorted[mid] : (sorted[mid - 1] + sorted[mid]) / 2;
  };
  return normalizeScorecard(
    Object.fromEntries(
      SCORE_DIMENSIONS.map(({ key }) => [
        key,
        {
          score: runs.length > 0 ? median(runs.map((run) => run[key])) : published[key].score,
          note: published[key].note,
        },
      ]),
    ) as Scorecard,
  );
}
